import React, { Component } from 'react';

import _get from 'lodash.get';
import _set from 'lodash.set';
import { arrayOf, element, string } from 'prop-types';

import { FieldsetAPI } from '@philips-emr/tws-components-api';
import { FieldVisibility } from '@philips-emr/tws-components-api/lib/constants';
import { AwesomeComponent, ComponentType, ContainerProvider } from '@philips-emr/tws-core';
import { bool, eventListener, expression, object, oneOf, oneOfType } from '@philips-emr/tws-core/lib/component/prop-types';
import { msg } from '@philips-emr/tws-core/lib/services/message-service';
import { classNames, enumValues, getLostProps, isFunction, propagateData } from '@philips-emr/tws-utils';

import './fieldset.scss';
import { CollapseArrow } from '..';

const { VIEWING, EDITING, BOTH } = FieldVisibility;

const acceptedTypes = [
  ComponentType.FORM,
];

@AwesomeComponent({ type: ComponentType.FORM, api: FieldsetAPI })
class Fieldset extends Component {

  static propTypes = {
    title: expression,
    entry: string,
    uuid: string,
    data: object.notEnumerable(),
    editing: bool.notEnumerable(),
    className: string,
    collapsible: bool,
    children: oneOfType([element, arrayOf(element)]).notEnumerable(),
    onChange: eventListener,
    visible: bool,
    visibility: oneOf(enumValues(FieldVisibility)),
  };

  static defaultProps = {
    collapsible: false,
    visible: true,
    visibility: BOTH,
  };

  state = {
    collapsed: false,
  };

  onClick = () => {
    const { collapsible } = this.props;

    collapsible && this.setState(({ collapsed }) => ({
      collapsed: !collapsed,
    }));
  };

  getClassName() {
    const { className } = this.props;

    return classNames({
      'c-fieldset': true,
      'is-collapsed': this.state.collapsed,
      [className]: className,
    });
  }

  isFieldsetVisible() {
    const { editing, visibility, visible } = this.props;

    if (!visible) {
      return false;
    }

    return (
      visibility === BOTH
      || (visibility === VIEWING && !editing)
      || (visibility === EDITING && editing)
    );
  }

  getValue() {
    const { data, entry } = this.props;
    return _get(data, entry) || data;
  }

  onChange = childEvent => {
    const { entry, onChange } = this.props;

    if (!isFunction(onChange)) {
      return;
    }

    if (!entry) {
      onChange(childEvent);
      return;
    }

    const value = this.getValue();
    const oldValue = { ...value };

    onChange({
      entry,
      value: _set({ ...value }, childEvent.entry, childEvent.value),
      oldValue,
    });
  };

  renderChildren() {
    const { children, className, editing } = this.props;
    const lostProps = getLostProps(Fieldset.propTypes, this.props);
    const value = this.getValue();

    return propagateData(children, value, {
      ...lostProps,
      className,
      editing,
      onChange: this.onChange,
    });
  }

  render() {
    if (!this.isFieldsetVisible()) return null;

    const { title, uuid, collapsible } = this.props;
    const { collapsed } = this.state;

    const Container = ContainerProvider.provide(this);

    return (
      <Container acceptedTypes={acceptedTypes} className={this.getClassName()} uuid={uuid}>
        {(title || collapsible) &&
        <div className="c-fieldset__header" onClick={this.onClick}>
          {title &&
          <div className="c-fieldset__title">{msg(title)}</div>}

          {collapsible &&
          <CollapseArrow className="c-fieldset__collapse-arrow" collapsed={collapsed} />}
        </div>}

        {(!collapsible || !collapsed) &&
        <div className="c-fieldset__content o-form" >
          {this.renderChildren()}
        </div>}
      </Container>
    );
  }
}

export default Fieldset;
